import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    Flex,
    Text,
    Button,
    Image,
    HStack,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {DeleteIcon, WarningIcon} from '../../../components/icons'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {FormattedMessage} from 'react-intl'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import {remCalc} from '../../../theme/_utils'

const parseDateInput = (dateInput) => {
    if (dateInput instanceof Date) {
        return new Date(dateInput)
    }

    const isNoSeparatorFormat = /^\d{14}$/.test(dateInput)
    if (isNoSeparatorFormat) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
        const [_, year, month, day] = dateInput.match(/(\d{4})(\d{2})(\d{2})/).map(Number)
        return new Date(year, month - 1, day) // month is 0-based
    }

    return new Date(dateInput)
}

const formatDate = (dateInput) => {
    const date = parseDateInput(dateInput)
    return date.toLocaleDateString('it-IT')
}

const RegisteredProductItem = ({
    product,
    styles,
    onEditClick,
    onDeleteClick,
    onCustomerSupportClick,
    onFAQsClick,
}) => {
    const {currency: activeCurrency} = useCurrency()
    const showWarrantyCertificate = useBooleanCustomPreference(
        'showRegisteredProductWarrantyCertificate',
    )
    const showExtendedWarrantyCertificate = useBooleanCustomPreference(
        'showRegisteredProductExtendedWarrantyCertificate',
    )
    const {data: productData} = useProduct({
        parameters: {
            id: product.skuKut,
            allImages: false,
            currency: activeCurrency,
        },
    })
    const smallImage = productData?.imageGroups?.find((item) => item.viewType === 'medium')
        ?.images[0]?.link

    return (
        <Box {...styles.registeredProductBox}>
            <Stack data-testid={`registered-product-${product.productid}`}>
                <Flex gap={6}>
                    <Image
                        {...styles.registeredProductImage}
                        src={smallImage}
                        alt={product?.c_variantIdentifier || product?.name || product?.productName}
                    />
                    <Stack spacing={10} flex="1" minW={0} overflowWrap="anywhere">
                        <Flex flexDir={'column'} gap={4} position={'relative'}>
                            <HStack {...styles.registredProductNameContainer}>
                                <Flex flexDir={'column'} gap={'4px'} minW={0}>
                                    <Text {...styles.registeredProductName}>
                                        {productData?.name}
                                    </Text>
                                    {product?.serialNumberKut && (
                                        <Text fontStyle={'body-small'} color={'text.default'}>
                                            <>SN: {product?.serialNumberKut}</>
                                        </Text>
                                    )}
                                </Flex>
                                <Button
                                    onClick={() => onDeleteClick(product)}
                                    data-testid="cart-item-remove-button"
                                    flexShrink={0}
                                >
                                    <DeleteIcon {...styles.registeredProductDeleteIcon} />
                                </Button>
                            </HStack>
                            {product?.verificationStatusKut === '111' && (
                                <Flex
                                    role="alert"
                                    alignItems={{base: 'flex-start', lg: 'center'}}
                                    gap={{base: remCalc(5), lg: remCalc(17)}}
                                    backgroundColor="background.neutral"
                                    border="1px solid"
                                    borderColor="border.light"
                                    p={{base: remCalc(10), lg: remCalc(26)}}
                                    flexDir={{base: 'column', lg: 'row'}}
                                >
                                    <WarningIcon flexShrink={0} />
                                    <Flex
                                        direction="column"
                                        alignItems="flex-start"
                                        gap={remCalc(5)}
                                        minW={0}
                                        maxW="100%"
                                    >
                                        <Text textStyle="body-small">
                                            <FormattedMessage
                                                id="account.registered_product.proof_validation_failed"
                                                defaultMessage="We haven't been able to validate your proof of purchase. Upload a new one or contact our customer support."
                                            />
                                        </Text>
                                        <Flex
                                            gap={{base: remCalc(5), lg: remCalc(20)}}
                                            flexWrap="wrap"
                                            flexDir={{base: 'column', lg: 'row'}}
                                            maxW="100%"
                                        >
                                            <Button
                                                {...styles.editRegisterProduct}
                                                variant="text-button"
                                                onClick={() => onEditClick(product)}
                                                data-testid="upload-new-proof-button"
                                                padding="0"
                                            >
                                                <FormattedMessage
                                                    id="account.registered_product.upload_new_proof"
                                                    defaultMessage="Upload a new proof of purchase"
                                                />
                                            </Button>
                                            <Button
                                                {...styles.editRegisterProduct}
                                                variant="text-button"
                                                onClick={onCustomerSupportClick}
                                                data-testid="proof-customer-support-button"
                                                padding="0"
                                            >
                                                <FormattedMessage
                                                    id="account.registered_product.contact_us"
                                                    defaultMessage="Contact us"
                                                />
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            )}
                            <Stack>
                                <Text {...styles.registeredProductPurchased}>
                                    <strong>
                                        <FormattedMessage
                                            id="account.registered_product.purchasedOn"
                                            defaultMessage="Purchased on: "
                                        />
                                    </strong>
                                    {product.purchaseData
                                        ? formatDate(product?.purchaseData)
                                        : formatDate(product?.referenceDate)}
                                </Text>
                                <Text {...styles.registeredProductPurchased}>
                                    <strong>
                                        <FormattedMessage
                                            id="account.registered_product.purchasedFrom"
                                            defaultMessage="Purchased from: "
                                        />
                                    </strong>
                                    {product?.sourceKut}
                                    {/* <br />
                                    <Button
                                        {...styles.editRegisterProduct}
                                        variant="text-button"
                                        data-testid="cart-item-edit-button-proof"
                                        padding="0"
                                        height={remCalc(14)}
                                    >
                                        <FormattedMessage
                                            id="account.registered_product.download_proof_of_purchase"
                                            defaultMessage="Download proof of purchase"
                                        />
                                    </Button> */}
                                </Text>
                                <Text {...styles.registeredProductRegistration}>
                                    <strong>
                                        <FormattedMessage
                                            id="account.registered_product.registeredOn"
                                            defaultMessage="Registered on: "
                                        />
                                    </strong>
                                    {formatDate(product?.registrationDateKut)}
                                </Text>
                                {product?.warrantyExpirationDateKut && (
                                    <Text {...styles.registeredProductPurchased}>
                                        <strong>
                                            <FormattedMessage
                                                id="account.registered_product.standardWarrantyExpiration"
                                                defaultMessage="Standard warranty expiration: "
                                            />
                                        </strong>
                                        {formatDate(product?.warrantyExpirationDateKut)}
                                        {showWarrantyCertificate && product?.serialId && (
                                            <>
                                                <br />
                                                <Button
                                                    as="a"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={`https://cloud.news.delonghi.com/standard-warranty?document_ID=${encodeURIComponent(
                                                        product.serialId,
                                                    )}`}
                                                    {...styles.editRegisterProduct}
                                                    variant="text-button"
                                                    data-testid="cart-item-edit-button-warranty"
                                                    padding="0"
                                                >
                                                    <FormattedMessage
                                                        id="account.registered_product.download_warranty_certificate"
                                                        defaultMessage="Download warranty certificate"
                                                    />
                                                </Button>
                                            </>
                                        )}
                                    </Text>
                                )}
                                <Button
                                    {...styles.editRegisterProduct}
                                    variant="text-button"
                                    onClick={() => onEditClick(product)}
                                    data-testid="cart-item-edit-button"
                                    padding="0"
                                >
                                    <FormattedMessage
                                        id="account.registered_product.edit"
                                        defaultMessage="Edit"
                                    />
                                </Button>
                            </Stack>
                            {product.delonghiCareTypeKut &&
                                product.delonghiCareExpirationDateKut && (
                                    <Flex {...styles.extendedWarrantyContainer}>
                                        <Flex {...styles.extendedWarrantyInnerContainer}>
                                            <Flex {...styles.extendedWarrantyTextContainer}>
                                                <Text {...styles.extendedWarrantyName}>
                                                    {product?.delonghiCareTypeKut}
                                                </Text>
                                                <Text {...styles.registeredProductPurchased}>
                                                    <strong>
                                                        <FormattedMessage
                                                            id="account.registered_product.extendedWarrantyExpiration"
                                                            defaultMessage="Extended warranty expiration: "
                                                        />
                                                    </strong>
                                                    {formatDate(
                                                        product?.delonghiCareExpirationDateKut,
                                                    )}
                                                    {showExtendedWarrantyCertificate &&
                                                        product?.serialId && (
                                                            <>
                                                                <br />
                                                                <Button
                                                                    as="a"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    href={`https://cloud.news.delonghi.com/warranty?document_ID=${encodeURIComponent(
                                                                        product.serialId,
                                                                    )}`}
                                                                    {...styles.editRegisterProduct}
                                                                    variant="text-button"
                                                                    data-testid="cart-item-edit-button-extended"
                                                                    padding="0"
                                                                >
                                                                    <FormattedMessage
                                                                        id="account.registered_product.download_extended_warranty_certificate"
                                                                        defaultMessage="Download extended warranty certificate"
                                                                    />
                                                                </Button>
                                                            </>
                                                        )}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                )}
                        </Flex>
                        <Flex
                            gap={4}
                            flexDirection={{
                                base: 'column',
                                md: 'row',
                            }}
                        >
                            <Button
                                {...styles.serviceButton}
                                variant="secondary"
                                onClick={onCustomerSupportClick}
                            >
                                <FormattedMessage
                                    id="account.registered_product.customerSupport"
                                    defaultMessage="Customer Support"
                                />
                            </Button>
                            <Button
                                {...styles.serviceButton}
                                variant="secondary"
                                onClick={onFAQsClick}
                            >
                                <FormattedMessage
                                    id="account.registered_product.faqs"
                                    defaultMessage="FAQs"
                                />
                            </Button>
                        </Flex>
                    </Stack>
                </Flex>
            </Stack>
        </Box>
    )
}

RegisteredProductItem.propTypes = {
    product: PropTypes.object.isRequired,
    styles: PropTypes.object.isRequired,
    onEditClick: PropTypes.func.isRequired,
    onDeleteClick: PropTypes.func.isRequired,
    onCustomerSupportClick: PropTypes.func.isRequired,
    onFAQsClick: PropTypes.func.isRequired,
}

export default RegisteredProductItem
export {formatDate}
